import { LIST_SALE_DOCUMENTS_GROUPS_FOR_ASYNC_SELECT } from "queries/centralApi/payments";
import { formatNumberWithSpaces } from "utils/formatNumberWithSpaces";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import useSepaOrderApi from "views/Clients/hooks/useSepaOrderApi";
import { Col, DatePicker, Form, Input, Row, Select } from "antd";
import usePaymentsApi from "views/Payments/hooks/usePaymentsApi";
import { DATE_FORMAT, DATE_FORMAT_API } from "config/settings";
import useNotification from "utils/hooks/useNotification";
import invoiceStore from "views/hooks/useInvoiceStore";
import { clientNeoFact } from "@neonumy/api-clients";
import { customToFixed } from "utils/toFixedNumber";
import useCoreStore from "views/hooks/useCoreStore";
import NumberFormat from "components/NumberFormat";
import { getFormError } from "utils/getFormError";
import { AsyncSelect } from "@neonumy/ant-design";
import { useTranslation } from "react-i18next";
const { TextArea } = Input;
import { useLazyQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { locale } from "utils/locale";
import dayjs from "dayjs";
import {
  CLIENT_HAS_RETURN_DOCUMENTS_UNPAID,
  LIST_SALE_DOCUMENT_SELECT,
} from "queries/neoFact/documents";

interface CreateUpdatePaymentFormPropsType {
  refetch: any;
  form: any;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
  id: ID;
  data: GetPaymentData | undefined;
}

const paginationInit = {
  offset: 0,
  limit: 100,
};
export default function CreateUpdatePaymentForm(
  props: CreateUpdatePaymentFormPropsType,
): React.ReactElement {
  const { data, id, refetch, form, setIsModalVisible } = props;

  const urlParams: RouteParams = useParams();
  const { t } = useTranslation();

  const { notificationError, notificationSuccessfullySaved } = useNotification();
  const { createUpdatePayment } = usePaymentsApi(id);

  const [paymentMethodsOptions, setPaymentMethodOptions] = useState<LabelValueType[]>([]);
  const { sepaOrdersData, listSepaOrders } = useSepaOrderApi();
  const companySettings = useCoreStore(state => state.companySettings);
  const setInvoiceRefetch = invoiceStore(state => state.setInvoiceRefetch);

  const sepaOptions = sepaOrdersData?.map(item => ({
    label: `${item?.paymentPeriod?.label} - ${item?.reference}`,
    value: item?.id,
  }));
  const saleDocumentSelect = Form.useWatch("group", form);
  const paymentMethodId = Form.useWatch("paymentMethodId", form);
  const paymentMethodCode =
    paymentMethodId === "null"
      ? "RE"
      : companySettings?.defaultPaymentMethods?.find(
          item => String(item.id) === String(paymentMethodId),
        )?.code;

  const client = Form.useWatch("group", form);
  const decimalPlaces = client?.currency?.decimalPlaces;
  const currencyCode = client?.currency?.code;

  const saleDocumentGroupFilter = data?.group?.id
    ? {
        id_In: [data?.group?.id],
      }
    : { saleDocuments_Status_In: ["to_be_paid", "partially_paid", "overdue"] };

  const [clientHasReturnDocuments] = useLazyQuery<
    ClientHasReturnDocumentsUnpaidResponse,
    ClientHasReturnDocumentsUnpaidVars
  >(CLIENT_HAS_RETURN_DOCUMENTS_UNPAID, {
    client: clientNeoFact,
  });

  useEffect(() => {
    if (client) {
      listSepaOrders({
        variables: {
          filter: {
            companyId: urlParams.companyId,
            clientId: client?.item?.client?.id,
            activeOnly: true,
          },
          ...paginationInit,
        },
      });
      const buildPaymentMethodOptions = async () => {
        const response = await clientHasReturnDocuments({
          variables: {
            companyId: urlParams.companyId,
            clientId: client?.item?.client?.id,
          },
        });
        const hasReturnDocuments = response?.data?.clientHasReturnDocumentsUnpaid;

        const avoirOption = {
          id: null,
          name: "Avoir",
          code: "RE",
        };

        setPaymentMethodOptions(
          ([...(companySettings?.defaultPaymentMethods ?? []), avoirOption].map(
            (item: Record<string, any>) => {
              return {
                label: item?.name,
                value: String(item?.id),
                code: item?.code,
                disabled: item?.code === "RE" && !hasReturnDocuments,
              };
            },
          ) ?? []) as unknown as React.SetStateAction<LabelValueType[]>,
        );
      };

      buildPaymentMethodOptions();
    }
  }, [client]);

  const getPaymentMethodCodeById = (paymentMethodId: ID | undefined) =>
    companySettings?.defaultPaymentMethods?.find(item => item.id === paymentMethodId)?.code;

  useEffect(() => {
    if (data && id) {
      const paymentMethodCode = getPaymentMethodCodeById(data?.paymentMethodId);
      const paymentMethodIsDD = paymentMethodCode === "DD";
      const paymentMethodIsAV = paymentMethodCode === "RE";
      const decimalPlaces = data?.group?.currency?.decimalPlaces;
      const currencyCode = data?.group?.currency?.code;

      form.setFields([
        {
          name: "paymentMethodId",
          value: id ? data?.paymentMethodId : data?.group?.saleDocuments[0]?.paymentMethod?.id,
        },
        {
          name: "amount",
          value: data?.amount,
        },
        {
          name: "date",
          value: dayjs(data?.date),
        },
        {
          name: "paymentMethodId",
          value: data?.paymentMethodId,
        },
        {
          name: "reference",
          value: data?.reference,
        },
        {
          name: "note",
          value: data?.note,
        },
        {
          name: "group",
          value: {
            label: `${data?.group?.saleDocuments[0]?.reference || ""} - ${Number(
              data?.group?.saleDocuments[0]?.totalPrice || 0,
            ).toFixed(decimalPlaces)} ${currencyCode} - ${
              data?.group?.saleDocuments[0]?.client?.name || ""
            }`,
            value: data?.group?.id,
            item: data?.group?.saleDocuments[0],
            currency: data?.group?.currency,
          },
        },
      ]);
      if (paymentMethodIsAV) {
        form.setFields([
          {
            name: "returnDocument",
            value: {
              label: `${data?.returnDocument?.reference} ${dayjs(data?.returnDocument?.date).format(
                DATE_FORMAT,
              )} (${Number(data?.returnDocument?.debt) * -1}€)`,
            },
          },
        ]);
      }
      if (paymentMethodIsDD) {
        form.setFields([
          {
            name: "sepa",
            value: id ? data?.sepa.id : data?.group?.saleDocuments[0]?.sepa?.id,
          },
        ]);
      }
    }
  }, [data]);

  const onFinish = async (values: Record<string, any>) => {
    const updateId = id ? { id } : {};
    let errors: any = [];
    const variables = {
      input: {
        group: values?.group?.value,
        amount: values.amount,
        sepa: values?.sepa,
        paymentMethodId: values.paymentMethodId === "null" ? null : values.paymentMethodId,
        reference: values.reference,
        note: values.note,
        document: values?.group?.item?.id,
        type: values?.group?.item?.type?.value,
        date: dayjs(values.date).format(DATE_FORMAT_API),
        returnDocument: values.returnDocument?.value,
        ...updateId,
      },
    };

    const response = await createUpdatePayment({ variables });

    errors = id ? response?.data?.updatePayment?.errors : response?.data?.createPayment?.errors;

    if (errors && errors.length > 0) {
      const fieldsData = getFormError(errors, values);
      notificationError(errors);
      form.setFields(fieldsData);
    } else {
      refetch();
      notificationSuccessfullySaved(t("Payment"));
      setIsModalVisible(false);
      setInvoiceRefetch(true);
      form.resetFields();
    }
  };

  const createOptionListSaleDocuments = (item: Record<string, any>) => {
    const decimalPlaces = item?.saleDocuments![0]?.group?.currency?.decimalPlaces;
    const currencyCode = item?.saleDocuments![0]?.group?.currency?.code;

    return {
      label: `${item?.saleDocuments![0]?.reference || ""} | ${formatNumberWithSpaces(
        customToFixed(
          Number(item?.saleDocuments![0]?.debt || item?.saleDocuments![0]?.totalPrice),
          decimalPlaces,
        ),
      )} ${currencyCode} - ${item?.saleDocuments![0]?.client?.name || ""}`,
      value: item?.saleDocuments![0]?.group?.id,
      item: item?.saleDocuments![0],
    };
  };

  const onValuesChange = (changedValues: Record<string, any>) => {
    if (changedValues?.group) {
      form.resetFields(["paymentMethodId", "sepa"]);
    }
  };

  return (
    <Form
      name="createUpdatePayment"
      layout="vertical"
      form={form}
      onFinish={onFinish}
      autoComplete="off"
      onValuesChange={onValuesChange}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24}>
          <Form.Item
            name="group"
            label={t("Group")}
            rules={[{ required: true, message: t("This field is required") }]}
          >
            <AsyncSelect
              isDisabled={!!data?.id || !!saleDocumentSelect}
              queryOptions={{
                client: clientNeoFact,
                dataName: "listSaleDocumentsGroups",
                query: LIST_SALE_DOCUMENTS_GROUPS_FOR_ASYNC_SELECT,
                variables: {
                  saleDocumentType: "PURCHASE_ORDER",
                  companyId_In: [urlParams?.companyId],
                  ...saleDocumentGroupFilter,
                  limit: 20,
                },
              }}
              optionsFormat={createOptionListSaleDocuments}
              isClearable={true}
              translations={{
                placeholder: t("Select invoice"),
                loadingMessage: t("Loading..."),
                noOptionsMessage: t("No Options"),
              }}
            />
          </Form.Item>
        </Col>
        {!!saleDocumentSelect && (
          <>
            <Col xs={24} sm={12}>
              <Form.Item
                name="amount"
                label={t("Amount")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <NumberFormat decimalPlaces={decimalPlaces} addonAfter={currencyCode} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="date"
                label={t("Date")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <DatePicker
                  locale={locale as any}
                  format={[DATE_FORMAT, "DDMMYYYY", "DD-MM-YYYY"]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="paymentMethodId"
                label={t("Payment method")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <Select options={paymentMethodsOptions} />
              </Form.Item>
            </Col>
            {paymentMethodCode === "DD" && (
              <Col xs={24} sm={12}>
                <Form.Item
                  name="sepa"
                  rules={[{ required: true, message: t("This field is required") }]}
                  label={t("Mandat")}
                >
                  <Select options={sepaOptions} />
                </Form.Item>
              </Col>
            )}
            {paymentMethodCode === "RE" && (
              <Col xs={24} sm={12}>
                <Form.Item
                  name="returnDocument"
                  rules={[{ required: true, message: t("This field is required") }]}
                  label={t("Return")}
                >
                  <AsyncSelect
                    hasQueryFilter={true}
                    queryOptions={{
                      client: clientNeoFact,
                      dataName: "listSaleDocuments",
                      query: LIST_SALE_DOCUMENT_SELECT,
                      variables: {
                        filter: {
                          search: "",
                          group_CompanyId: urlParams.companyId,
                          group_ClientId: client?.item?.client?.id,
                          status_In: ["to_be_paid", "partially_paid"],
                          type: "return",
                        },
                        limit: 100,
                      },
                    }}
                    optionsFormat={item => ({
                      label: `${item?.reference} ${dayjs(item.date).format(DATE_FORMAT)} (${
                        item.debt * -1
                      }€)`,
                      value: item.id,
                    })}
                    translations={{
                      loadingMessage: t("Loading"),
                      noOptionsMessage: t("No Options"),
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={12}>
              <Form.Item name="reference" label={t("Reference")}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24}>
              <Form.Item name="note" label={t("Description")}>
                <TextArea style={{ height: 50 }} />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
    </Form>
  );
}
